import { memo, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { classNames } from "helpers";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import Logo from "ui/modules/public/components/Logo/Logo";

interface NewPriceIntroProps {
  show: boolean;
}

const NewPriceIntro: React.FC<NewPriceIntroProps> = ({ show }) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!show) {
      setPhase(0);
      return;
    }

    const timer = setTimeout(() => setPhase(1), 150);
    return () => clearTimeout(timer);
  }, [show]);

  useEffect(() => {
    if (phase > 0 && lottieRef.current) {
      lottieRef.current.setSpeed(0.5);
      lottieRef.current.goToAndPlay(0);
    }
  }, [phase]);

  const handleLoopComplete = () => {
    if (lottieRef.current) {
      lottieRef.current.stop();
      const totalFrames = lottieRef.current.getDuration(true) ?? 0;
      if (totalFrames > 0) {
        lottieRef.current.goToAndStop(totalFrames - 1, true);
      }
    }
  };

  const isTextVisible = phase > 0;

  return ReactDOM.createPortal(
    <div
      className={classNames(
        "transition-opacity duration-300",
        show ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}
    >
      <div
        className={`bg-blue-700 fixed top-0 left-0 bottom-0 z-[35] flex transition-all duration-500 ease-in-out ${
          phase === 0 ? "right-full" : "right-1/2"
        }`}
      >
        <div className="relative w-full h-screen flex flex-col items-center justify-center overflow-hidden">
          <Logo
            variant="light"
            animated
            className={`absolute top-8 left-8 transition-opacity duration-700 delay-300 ${
              isTextVisible ? "opacity-100" : "opacity-0"
            }`}
          />
          <Lottie
            lottieRef={lottieRef}
            animationData={invoiceAnimation}
            autoplay={false}
            loop={true}
            onLoopComplete={handleLoopComplete}
            className="w-[500px] h-auto"
          />
          <div
            className={`text-center transition-all duration-700 ease-out delay-500 max-w-md ${
              isTextVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-16"
            }`}
          >
            <h1 className="text-4xl leading-[46px] font-bold mb-3 text-white">
              Izračunajte cijenu novog vozila!
            </h1>
            <p className="text-lg opacity-80 text-blue-50">
              Unesite podatke o vozilu i dodatnoj opremi kako bi sustav
              automatski izračunao cijenu novog vozila.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default memo(NewPriceIntro);
